<template>
    <LandingPageLayout
        v-bind="landingPageSharedData.getLandingPageLayoutProps()"
    >
        <FeaturePageHeroSection
            title="Flexible scheduling controls for client-facing offers"
            subtitle="Shape exactly how and when clients can book so your calendar stays realistic, focused, and easy to manage."
            buttonLabel="Set scheduling rules"
            :buttonUrl="heroRegisterUrl"
        />

        <section class="border-b border-slate-200 bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div class="grid gap-10 lg:grid-cols-2 lg:items-start">
                    <div>
                        <FeaturePageSectionHeader
                            title="Fine-grained scheduling policy"
                            subtitle="Control spacing, lead time, and booking horizon so each offer fits the way you actually work."
                            subtitleClass="mt-3 text-slate-600"
                        />

                        <FeaturePagePrimaryButton
                            class="mt-7"
                            label="Apply scheduling controls"
                            :href="ctaRegisterUrl"
                        />
                    </div>

                    <div>
                        <FeaturePageMockPanel
                            title="Scheduling Policy"
                            badgeText="Configurable"
                        >
                            <FeaturePageInfoCard
                                v-for="schedulingPolicy in schedulingPolicies"
                                :key="schedulingPolicy.title"
                                :title="schedulingPolicy.title"
                                :description="schedulingPolicy.description"
                                containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />
                        </FeaturePageMockPanel>
                    </div>
                </div>
            </div>
        </section>

        <section class="border-b border-slate-200 bg-white">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Scheduling controls"
                    subtitle="Use policy-level controls to shape booking behavior, protect focus time, and keep your availability realistic."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="schedulingControl in schedulingControls"
                        :key="schedulingControl.title"
                        :title="schedulingControl.title"
                        :description="schedulingControl.description"
                        containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                        :badgeText="
                            schedulingControl.isPlanned ? 'Planned' : null
                        "
                        badgeVariant="upcoming"
                        badgeSize="xs"
                        badgePosition="bottom"
                        badgeClass="mt-2"
                    />
                </div>

                <div
                    class="inline-flex gap-1 mt-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900"
                >
                    <span class="font-bold">Pro tip:</span> Overlapping
                    availability can be configured for each service to support
                    more advanced scheduling windows.
                </div>
            </div>
        </section>

        <section class="bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Sample use cases"
                    subtitle="Scheduling controls help solo experts stay bookable without losing control of their calendar."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="sampleUseCase in sampleUseCases"
                        :key="sampleUseCase.title"
                        :title="sampleUseCase.title"
                        :description="sampleUseCase.description"
                        containerClass="rounded-xl border border-slate-200 bg-white p-5"
                        titleClass="text-lg font-bold"
                        descriptionClass="mt-2 text-sm text-slate-600"
                    />
                </div>
            </div>
        </section>
    </LandingPageLayout>
</template>

<script setup lang="ts">
    import { UtmContentEnum } from '@/Shared/Service/Tracking/Utm/UtmEnums'
    import FeaturePagePrimaryButton from '@/Marketing/LandingPage/Component/FeaturePage/PrimaryButton.vue'
    import FeaturePageHeroSection from '@/Marketing/LandingPage/Component/FeaturePage/HeroSection.vue'
    import FeaturePageInfoCard from '@/Marketing/LandingPage/Component/FeaturePage/InfoCard.vue'
    import FeaturePageMockPanel from '@/Marketing/LandingPage/Component/FeaturePage/MockPanel.vue'
    import FeaturePageSectionHeader from '@/Marketing/LandingPage/Component/FeaturePage/SectionHeader.vue'
    import LandingPageSharedData from '@/Marketing/LandingPage/Entity/LandingPageSharedData'
    import LandingPageLayout from '@/Marketing/LandingPage/Shared/LandingPageLayout.vue'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface LandingPageProps {
        landingPageSharedDataRawData: any
    }

    interface SchedulingControl {
        title: string
        description: string
        isPlanned: boolean
    }

    interface SchedulingPolicy {
        title: string
        description: string
    }

    interface SampleUseCase {
        title: string
        description: string
    }

    const props = defineProps<LandingPageProps>()

    const landingPageSharedData = LandingPageSharedData.hydrate(
        props.landingPageSharedDataRawData,
    )
    const urlFactory = new UrlFactory()
    const heroRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.FlexibleSchedulingHero,
    )
    const ctaRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.FlexibleSchedulingCta,
    )

    const schedulingControls: SchedulingControl[] = [
        {
            title: 'Fine-grained control',
            description:
                'Apply scheduling rules per service so each offer follows the correct availability policy.',
            isPlanned: false,
        },
        {
            title: 'Buffer between meetings',
            description:
                'Prevent back-to-back overload by adding prep and recovery time around each booking.',
            isPlanned: false,
        },
        {
            title: 'Minimum time in advance',
            description:
                'Require a minimum lead time before sessions can be scheduled, configured in hours.',
            isPlanned: false,
        },
        {
            title: 'Maximum time in advance',
            description:
                'Limit how far ahead clients can book to keep schedules realistic and manageable, configured in days.',
            isPlanned: false,
        },
        {
            title: 'One-time scheduling links',
            description:
                'Generate links that can be used for a single booking and then expire automatically.',
            isPlanned: true,
        },
        {
            title: 'Round-robin scheduling',
            description:
                'Planned for future multi-host use cases where bookings should be distributed automatically.',
            isPlanned: true,
        },
        {
            title: 'Collective scheduling',
            description:
                'Planned for sessions where more than one host needs to join the same booking.',
            isPlanned: true,
        },
    ]

    const schedulingPolicies: SchedulingPolicy[] = [
        {
            title: 'Cadence guardrails',
            description:
                'Use lead-time and horizon rules to avoid urgent same-day surprises and long-range calendar drift.',
        },
        {
            title: 'Capacity protection',
            description:
                'Buffers and advanced availability controls protect host focus and reduce scheduling fatigue.',
        },
        {
            title: 'Future routing models',
            description:
                'Round-robin and collective scheduling are planned for advanced multi-host scenarios later on.',
        },
    ]

    const sampleUseCases: SampleUseCase[] = [
        {
            title: 'Deep-work consultants',
            description:
                'Add meeting buffers and strict lead time so client calls do not break focused work blocks.',
        },
        {
            title: 'Busy coaching practices',
            description:
                'Use per-service scheduling controls so discovery calls, paid sessions, and workshops each follow the right booking rules.',
        },
        {
            title: 'Advisory sessions with a guest expert',
            description:
                'Plan ahead for multi-host bookings when a specialist or co-host needs to join certain sessions.',
        },
    ]
</script>
